import { baseApi } from '../../../../store/api/baseApi';

// ========== Query Arg Types ==========

export interface KvsResultQueryArgs {
  year: number;
  term: number;
}

export interface TopPerformersQueryArgs extends KvsResultQueryArgs {
  order?: 'asc' | 'desc';
}

export interface StudentDetailQueryArgs {
  year: number;
  term: number;
  studentid: number;
}

export interface RoQueryArgs {
  year: number;
  term: number;
  roid: number;
}

export interface RoTopPerformersQueryArgs extends RoQueryArgs {
  order?: 'asc' | 'desc';
}

export interface SchoolDetailQueryArgs {
  year: number;
  term: number;
  kvid: number;
}

export interface TopPerformersSchoolsQueryArgs {
  year: number;
  term: number;
  order?: 'asc' | 'desc';
  limit?: number;
}

// ========== Response Types ==========

export interface KvsResultSummaryClassData {
  totalStudents: number;
  avgMarks: number;
  avgMarksPercentage: number;
  needSupport: number;
  excellentStudents: number;
}

export interface KvsResultSummaryResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    totalStudents: number;
    avgMarks: number;
    avgMarksPercentage: number;
    needSupport: number;
    excellentStudents: number;
    byClass: Record<string, KvsResultSummaryClassData>;
  };
  message: string;
}

export interface SubjectDistributionItem {
  subjectTitle: string;
  avgGrandTotal: number;
  avgWeightage: number;
  totalStudents: number;
}

export interface KvsResultSubjectwiseResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    overall: SubjectDistributionItem[];
    byClass: Record<string, SubjectDistributionItem[]>;
  };
  message: string;
}

export interface PerformanceDistribution {
  excellent: number;
  good: number;
  average: number;
  belowAverage: number;
  total: number;
}

export interface KvsResult3to5Response {
  success: boolean;
  data: {
    year: number;
    term: string;
    overall: PerformanceDistribution;
    byClass: Record<string, PerformanceDistribution>;
  };
  message: string;
}

export interface RegionTopBottomSchool {
  schoolID: number;
  schoolName: string;
  avgMarksPercentage: number;
  totalStudents: number;
}

export interface RegionCompetencyItem {
  roID: number;
  roName: string;
  avgMarksPercentage: number;
  totalStudents: number;
  topSchool: RegionTopBottomSchool;
  bottomSchool: RegionTopBottomSchool;
}

export interface KvsResultRegionwiseResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    regions: RegionCompetencyItem[];
    byClass?: Record<string, RegionCompetencyItem[]>;
  };
  message: string;
}

export interface KvsResultStudentDetail {
  studentID: number;
  firstName: string;
  middleName?: string;
  lastName?: string;
  rollNo: string | number | null;
  cbseRollNo?: string | null;
  academicSession?: string | null;
  stream?: number | null;
  kvID?: number;
  schoolName: string;
  class: string | number;
  section: string;
  marks: number;
  result?: number;
  resultStatus?: number;
  workingDay: number;
  presentDay: number;
  term?: string;
  year?: number;
  [key: string]: any;
}

export interface KvsResultStudentDetailResponse {
  success: boolean;
  data: KvsResultStudentDetail[];
  message?: string;
}

// ─── School Detail ───────────────────────────────────────────────────────────

export interface SchoolClassStudent {
  studentID: number;
  studentName: string;
  rollNo: string | number | null;
  section: string;
  marks: number;
  workingDay: number;
  presentDay: number;
  result?: number;
  resultStatus?: number;
  cbseRollNo?: string | null;
  academicSession?: string | null;
  stream?: number | null;
  [key: string]: any;
}

export interface SchoolClassData {
  totalStudents: number;
  avgMarks: number;
  needSupport: number;
  excellentStudents: number;
  students: SchoolClassStudent[];
  [key: string]: any;
}

export interface KvsResultSchoolDetailData {
  kvID?: number;
  schoolID: number;
  schoolName: string;
  roID?: number;
  roName?: string;
  year?: number;
  term?: string;
  byClass: Record<string, SchoolClassData>;
  [key: string]: any;
}

export interface KvsResultSchoolDetailResponse {
  success: boolean;
  data: KvsResultSchoolDetailData;
  message?: string;
}

// ─── RO-level responses ───────────────────────────────────────────────────────

export interface KvsResultRoSummaryResponse {
  success: boolean;
  data: {
    roID?: number;
    roName?: string;
    totalStudents: number;
    avgMarks: number;
    needSupport: number;
    excellentStudents: number;
    [key: string]: any;
  };
  message?: string;
}

export interface KvsResultRoSubjectwiseResponse {
  success: boolean;
  data: {
    overall?: SubjectDistributionItem[];
    [key: string]: any;
  };
  message?: string;
}

export interface KvsResultRo3to5Response {
  success: boolean;
  data: {
    overall?: PerformanceDistribution;
    [key: string]: any;
  };
  message?: string;
}

/**
 * Pass/fail counts derived from the `result` column (1 = pass, 2 = fail,
 * 0 = not declared). `passPercentage` is pass / (pass + fail) × 100 and is
 * null — not 0 — when nothing was declared.
 */
export interface PassFailCounts {
  passed: number;
  failed: number;
  notDeclared: number;
  passPercentage: number | null;
}

export interface RoSchool3to5ClassData extends PassFailCounts {
  totalStudents: number;
  avgMarks: number;
  needSupport: number;
  excellentStudents: number;
}

export interface RoSchool3to5Item extends PassFailCounts {
  kvID: number;
  schoolID: number;
  schoolName: string;
  totalStudents: number;
  avgMarks: number;
  needSupport: number;
  excellentStudents: number;
  byClass: Record<string, RoSchool3to5ClassData | null>;
}

export interface KvsResult3to5RoDetailsResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    roID: number;
    roName: string;
    totalSchools: number;
    schools: RoSchool3to5Item[];
  };
  message: string;
}

export interface RoSchoolCompetencyItem {
  schoolID: number;
  schoolName: string;
  avgMarksPercentage: number;
  totalStudents: number;
  [key: string]: any;
}

export interface KvsResultRoCompetencyResponse {
  success: boolean;
  data: {
    schools?: RoSchoolCompetencyItem[];
    [key: string]: any;
  };
  message?: string;
}

export interface TopPerformerSchool {
  rank?: number;
  schoolID: number;
  schoolName: string;
  roID: number;
  roName?: string;
  avgMarksPercentage: number;
  totalStudents: number;
  [key: string]: any;
}

export interface KvsResultRoTopPerformersResponse {
  success: boolean;
  data: {
    schools?: TopPerformerSchool[];
    [key: string]: any;
  };
  message?: string;
}

export interface KvsResultTopPerformersSchoolsResponse {
  success: boolean;
  data: {
    schools?: TopPerformerSchool[];
    [key: string]: any;
  };
  message?: string;
}

export interface TopPerformerRegion {
  rank: number;
  roID: number;
  roName: string;
  avgMarksPercentage: number;
  totalStudents: number;
}

export interface KvsResultTopPerformersResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    order: string;
    regions: TopPerformerRegion[];
  };
  message: string;
}

// ─── All-class summary (Stage Selection screen) ────────────────────────────────

export interface AllClassStageSummary {
  totalStudents: number;
  avgMarksPercentage: number;
  excellentStudents: number;
  needSupport: number;
  excellentRate: number;
  supportRate: number;
}

/**
 * Session label for a results year. Results are stored by the year they were
 * uploaded — the year the session ENDED — so 2026 holds session 2025-26 (its rows
 * date from March–September 2026; normalizeKvsResultYear in the backend maps
 * session '25 to 2026, and the Foundation card reads HPC academicYear 2025 for it).
 */
export const resultsSessionLabel = (year: number): string => `${year - 1}-${String(year).slice(-2)}`;

export interface KvsResultAllClassSummaryQueryArgs {
  year: number;
  /** Foundation HPC competency term (1|2). Omit to use the backend default. */
  term?: number;
  term3to5?: number;
  term6to8?: number;
  term9n11?: number;
}

export interface KvsResultAllClassSummaryResponse {
  success: boolean;
  data: {
    year: number;
    overview: {
      totalStudents: number;
      nationalAverage: number;
      excellenceRate: number;
      supportRate: number;
    };
    stages: {
      foundationStage: AllClassStageSummary;
      preparatoryStage: AllClassStageSummary;
      middleStage: AllClassStageSummary;
      secondaryStage: AllClassStageSummary;
    };
  };
  message: string;
}

// ========== RTK Query Endpoints ==========

export const kvsResultApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getKvsResultSummary: build.query<KvsResultSummaryResponse, KvsResultQueryArgs>({
      query: ({ year, term }) => `/kvsresult/summary?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResultSubjectwise: build.query<KvsResultSubjectwiseResponse, KvsResultQueryArgs>({
      query: ({ year, term }) => `/kvsresult/subjectwise-distribution?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult3to5: build.query<KvsResult3to5Response, KvsResultQueryArgs>({
      query: ({ year, term }) => `/kvsresult/3to5-distribution?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResultRegionwise: build.query<KvsResultRegionwiseResponse, KvsResultQueryArgs>({
      query: ({ year, term }) => `/kvsresult/regionwise-competency?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResultTopPerformers: build.query<KvsResultTopPerformersResponse, TopPerformersQueryArgs>({
      query: ({ year, term, order = 'desc' }) =>
        `/kvsresult/top-performers-regions?term=${term}&year=${year}&order=${order}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResultStudentDetail: build.query<KvsResultStudentDetailResponse, StudentDetailQueryArgs>({
      query: ({ year, term, studentid }) =>
        `/kvsresult/3to5-student-detail?term=${term}&year=${year}&studentid=${studentid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResultSchoolDetail: build.query<KvsResultSchoolDetailResponse, SchoolDetailQueryArgs>({
      query: ({ year, term, kvid }) =>
        `/kvsresult/3to5-school-detail?term=${term}&year=${year}&kvid=${kvid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResultRoSummary: build.query<KvsResultRoSummaryResponse, RoQueryArgs>({
      query: ({ year, term, roid }) =>
        `/kvsresult/ro-summary?term=${term}&year=${year}&roid=${roid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResultRoSubjectwise: build.query<KvsResultRoSubjectwiseResponse, RoQueryArgs>({
      query: ({ year, term, roid }) =>
        `/kvsresult/ro-subjectwise-distribution?term=${term}&year=${year}&roid=${roid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResultRo3to5: build.query<KvsResultRo3to5Response, RoQueryArgs>({
      query: ({ year, term, roid }) =>
        `/kvsresult/ro-3to5-distribution?term=${term}&year=${year}&roid=${roid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult3to5RoDetails: build.query<KvsResult3to5RoDetailsResponse, RoQueryArgs>({
      query: ({ year, term, roid }) =>
        `/kvsresult/3to5-result-rodetails?term=${term}&year=${year}&roid=${roid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResultRoCompetency: build.query<KvsResultRoCompetencyResponse, RoQueryArgs>({
      query: ({ year, term, roid }) =>
        `/kvsresult/ro-competency?term=${term}&year=${year}&roid=${roid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResultRoTopPerformers: build.query<KvsResultRoTopPerformersResponse, RoTopPerformersQueryArgs>({
      query: ({ year, term, roid, order = 'desc' }) =>
        `/kvsresult/ro-top-performers?term=${term}&year=${year}&roid=${roid}&order=${order}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResultTopPerformersSchools: build.query<KvsResultTopPerformersSchoolsResponse, TopPerformersSchoolsQueryArgs>({
      query: ({ year, term, order = 'desc', limit }) =>
        `/kvsresult/top-performers-schools?term=${term}&year=${year}&order=${order}${limit !== undefined ? `&limit=${limit}` : ''}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResultAllClassSummary: build.query<KvsResultAllClassSummaryResponse, KvsResultAllClassSummaryQueryArgs>({
      query: ({ year, term, term3to5, term6to8, term9n11 }) => {
        const params = new URLSearchParams({ year: String(year) });
        if (term !== undefined) params.set('term', String(term));
        if (term3to5 !== undefined) params.set('term3to5', String(term3to5));
        if (term6to8 !== undefined) params.set('term6to8', String(term6to8));
        if (term9n11 !== undefined) params.set('term9n11', String(term9n11));
        return `/kvsresult/all-class-summary?${params.toString()}`;
      },
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetKvsResultSummaryQuery,
  useGetKvsResultSubjectwiseQuery,
  useGetKvsResult3to5Query,
  useGetKvsResultRegionwiseQuery,
  useGetKvsResultTopPerformersQuery,
  useGetKvsResultStudentDetailQuery,
  useGetKvsResultSchoolDetailQuery,
  useGetKvsResultRoSummaryQuery,
  useGetKvsResultRoSubjectwiseQuery,
  useGetKvsResultRo3to5Query,
  useGetKvsResult3to5RoDetailsQuery,
  useGetKvsResultRoCompetencyQuery,
  useGetKvsResultRoTopPerformersQuery,
  useGetKvsResultTopPerformersSchoolsQuery,
  useGetKvsResultAllClassSummaryQuery,
} = kvsResultApi;
